/* eslint-disable max-lines */
import { isDate, isDateString, isString, isUUID } from 'class-validator';
import { camelCase, isNil, isUndefined } from 'lodash';
import { DateTime } from 'luxon';
import PasswordValidator from 'password-validator';

import { userConstants } from '../constants';
import {
  hasOnlyCCEmails,
  IAppointment,
  isAppointmentCancellation,
  isStatusChange,
  isStatusOnlyChange,
  isStatusTimelineChange
} from './appointment';
import { hasOnlyTags, INovaEntity, NovaId } from './base';
import { ICompany } from './company';
import { GATE_MANAGEMENT_USER_EMAIL } from './checkin';
import { Maybe } from './globals';
import { t } from './i18n';
import { isTrulyEmpty, isValidInternalEmail, namesToFullName } from './js-helpers';
import { GlobalLimits } from './limits';
import { IHasOrgId } from './org';
import { diffBetweenDates } from './timezone';
import { secondsIn } from './units';

export const MAX_WAREHOUSE_PER_USER = GlobalLimits.MAX_WAREHOUSE_PER_USER.value;
export const PASSWORD_SALT_LENGTH = 8;
export const SMARTGATE_USER_FIRST_NAME = 'Smartgate';
export const PASSWORD_RESET_EMAIL_THROTTLE_IN_MINUTES = 5;

export interface IUser extends INovaEntity, IHasOrgId {
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  phone?: string;
  extension?: string;
  isEmailVerified: boolean;
  role: UserRole;
  appointments?: IAppointment[];
  invalidLoginAttempts?: number;
  companyId?: NovaId;
  company?: ICompany;
  orgId?: NovaId;
  orgIsActive?: boolean;
  orgName?: string;
  warehouseAccessList?: NovaId[];
  tcConfirmedAt?: Date;
  lastLoginAt?: Date;
  passwordResetRequired?: boolean;
  passwordResetEmailSentAt?: Date;
  internalLoginUserId?: string;
  preferredLanguage?: string | null;
  trustTier?: CarrierTrustTier | null;
}

// IMPORTANT: The order of the individual roles
// below matters! Each role should be "more powerful" than
// the one below it.
export enum UserRole {
  // This role should be used sparingly, maybe even temporary with a cron to lower the role each day
  GOD = 'role_god',
  // CSAs, and others use this role to allow them to modify users and loginAs other users
  INTERNAL = 'role_internal',

  // Standard roles for warehouse users
  OWNER = 'role_owner',
  ADMIN = 'role_admin',
  OPERATOR = 'role_operator',
  ATTENDANT = 'role_attendant',
  SPECTATOR = 'role_spectator',
  YARD_DRIVER = 'role_yard_driver',

  // Standard roles for carrier users
  CARRIER_ADMIN = 'role_carrier_admin',
  CARRIER = 'role_carrier',

  // Initial role before being promoted.
  BASE = 'role_base'
}

/** User role values included in public OpenAPI (Swagger) schemas; excludes internal-only roles. */
export const OPENAPI_DOCUMENTED_USER_ROLES: readonly UserRole[] = Object.freeze(
  (Object.values(UserRole) as UserRole[]).filter(
    role => role !== UserRole.GOD && role !== UserRole.INTERNAL
  )
);

/**
 * Carrier trust tier (SUR-383 / SUR-384). Drives per-endpoint rate limits for carrier users.
 * - `low`: default for every account.
 * - `high`: proven carriers; set by the daily promotion job or manually.
 * - `restricted`: set manually only; same limits as `low`, never promoted by the job.
 */
export enum CarrierTrustTier {
  LOW = 'low',
  HIGH = 'high',
  RESTRICTED = 'restricted'
}

export enum UserType {
  CARRIER = 'carrierUser',
  INTERNAL = 'internalUser',
  WAREHOUSE = 'warehouseUser',
  BASE = 'baseUser'
}

export const CARRIER_ROLES = Object.freeze([
  UserRole.BASE,
  UserRole.CARRIER,
  UserRole.CARRIER_ADMIN
]) as UserRole[];

export function getUserType(user: Maybe<IUser>): UserType {
  if (isNil(user)) {
    throw new Error(`Core: getUserType(): invalid User ${user}`);
  }
  // if they have an internal role, then they are internal no matter what.
  if ([UserRole.GOD, UserRole.INTERNAL].includes(user.role)) {
    return UserType.INTERNAL;
  }
  // then if they have a warehouse role, they are a warehouse user (other checks should enforce them to have an orgId)
  if (
    [
      UserRole.OWNER,
      UserRole.ADMIN,
      UserRole.OPERATOR,
      UserRole.ATTENDANT,
      UserRole.SPECTATOR,
      UserRole.YARD_DRIVER
    ].includes(user.role)
  ) {
    return UserType.WAREHOUSE;
  }
  // failing the checks above, we want to determine if they are a carrier or a base user
  if (
    !isTrulyEmpty(user.companyId) ||
    [UserRole.CARRIER, UserRole.CARRIER_ADMIN].includes(user.role)
  ) {
    return UserType.CARRIER;
  }

  return UserType.BASE;
}

export function getUserFullName(user: Maybe<IUser>): string {
  return namesToFullName(user?.firstName, user?.lastName) || 'Unknown user';
}

export function getRoleActionError(action?: string) {
  return action
    ? t('core.user.errors.yourRoleDoesNotAllowThisAction', { action })
    : t('core.user.errors.yourRoleDoesNotAllowYouToPerformThisAction');
}

export function canUserAccessWarehouse(user: IUser, warehouseId: NovaId, warehouseOrgId?: NovaId) {
  /*
   warehouseOrgId is optional so this method can keep its original functionality when it is too
   difficult to reasonably get the warehouse.orgId
   This allows us to be more correct and secure when we are able, but use the current functionality
   when we are not.
    - Carriers and Internal users have access to all warehouses.
    - Base users do not have access to ANY warehouses.
    - WarehouseUsers should only have access to 1) warehouses within their org and 2) not restricted FROM
    the warehouse via warehouseAccessList or permitted via their role
   */
  const userType = getUserType(user);

  if ([UserType.CARRIER, UserType.INTERNAL].includes(userType)) {
    return true;
  }

  if (UserType.BASE === userType) {
    return false;
  }

  // this is the added security to ensure the warehouse and the user are in the same org
  if (warehouseOrgId && user.orgId !== warehouseOrgId) {
    return false;
  }

  const warehouseAccessList = isUndefined(user.warehouseAccessList) ? [] : user.warehouseAccessList;
  return !isUserWarehouseRestricted(user) || warehouseAccessList.includes(warehouseId);
}

export function isUserWarehouseRestricted(user: Maybe<IUser>) {
  if (isNil(user)) {
    throw new Error(`Core: isUserWarehouseRestricted(): invalid User ${user}`);
  }
  return canUserRoleHaveWarehouseAccessList(user.role) && Array.isArray(user.warehouseAccessList);
}

// This is a list of User Roles that *can* be further limited to a subset of Warehouses. The other roles
// simply follow the pre-existing Org access rules.
export function canUserRoleHaveWarehouseAccessList(role: UserRole) {
  return (
    role &&
    [
      UserRole.ADMIN,
      UserRole.OPERATOR,
      UserRole.ATTENDANT,
      UserRole.SPECTATOR,
      UserRole.YARD_DRIVER
    ].includes(role)
  );
}

export function isValidInternalLoginAttempt(email: string, orgId: string) {
  return Boolean(isValidInternalEmail(email) && orgId === userConstants.defaultInternalOrgId);
}

export function getJwtExpirationTimeByRole(role: UserRole) {
  return {
    // 1 hour
    [UserRole.GOD]: secondsIn.hour,
    // 1 day
    [UserRole.INTERNAL]: secondsIn.day,
    // 3 days
    [UserRole.OWNER]: 3 * secondsIn.day,
    [UserRole.ADMIN]: 3 * secondsIn.day,
    // 30 days
    [UserRole.OPERATOR]: 30 * secondsIn.day,
    [UserRole.ATTENDANT]: 30 * secondsIn.day,
    [UserRole.SPECTATOR]: 30 * secondsIn.day,
    [UserRole.YARD_DRIVER]: 30 * secondsIn.day,
    [UserRole.CARRIER_ADMIN]: 30 * secondsIn.day,
    [UserRole.CARRIER]: 30 * secondsIn.day,
    [UserRole.BASE]: 30 * secondsIn.day
  }[role];
}

export function getValidInviteeRoles() {
  return [
    UserRole.OWNER,
    UserRole.ADMIN,
    UserRole.OPERATOR,
    UserRole.ATTENDANT,
    UserRole.SPECTATOR,
    UserRole.YARD_DRIVER
  ];
}

export function userRoleDescription(role: UserRole) {
  return {
    // Placeholders for GOD and INTERNAL since this doesn't get displayed anywhere at the moment
    [UserRole.GOD]: 'All permissions and full control over all accounts.',
    [UserRole.INTERNAL]: 'GOD role with less control.',

    [UserRole.OWNER]: 'All permissions and full control over the Organization account.',
    [UserRole.ADMIN]:
      'Create/edit/delete Appointments, Warehouses, Load Types, Docks, and Users (except Owners).',
    [UserRole.OPERATOR]: 'Create/edit/cancel Appointments.',
    [UserRole.ATTENDANT]: 'Edit Appointment Status, tags, and subscribers.',
    [UserRole.SPECTATOR]: 'View only. No create or edit actions allowed.',
    [UserRole.YARD_DRIVER]: 'Warehouse driver, has limited access to the application.',

    [UserRole.CARRIER_ADMIN]: 'Carrier administrator.',
    [UserRole.CARRIER]: 'Carrier user',

    role_base: 'Base user account.'
  }[role];
}

export function getTranslatedUserRoleDescription(role: UserRole) {
  const raw = userRoleDescription(role);
  if (!raw) {
    return '';
  }

  const key = camelCase(raw);
  return t(`core.user.descriptions.${key}`);
}

export function userRoleToText(role: UserRole) {
  return {
    role_god: 'God',
    role_internal: 'Internal',

    role_owner: 'Owner',
    role_admin: 'Admin',
    role_operator: 'Operator',
    role_attendant: 'Attendant',
    role_spectator: 'Spectator',
    role_yard_driver: 'Yard Driver',

    role_carrier_admin: 'Carrier Admin',
    role_carrier: 'Carrier',

    role_base: 'Base'
  }[role];
}

export function hasEmptyOrgId(user: Maybe<IUser>): boolean {
  return validateUser(user) && !user.orgId;
}

export function isOmnipotentUser(user: Maybe<IUser>): boolean {
  return validateUser(user) && user.role === UserRole.GOD;
}

export function isIdenticalUser(user1: IUser, user2: IUser): boolean {
  return Boolean(
    validateUser(user1) && validateUser(user2) && user1.id && user2.id && user1.id === user2.id
  );
}

export function isStrictInternalRole(role: UserRole): boolean {
  return validateRole(role) && role === UserRole.INTERNAL;
}

export function isInternalRole(role: UserRole): boolean {
  return validateRole(role) && (role === UserRole.GOD || role === UserRole.INTERNAL);
}

export function isSmartgateUser(user: Maybe<IUser>): boolean {
  if (isNil(user)) {
    throw new Error(`Core: isSmartgateUser(): invalid User ${user}`);
  }
  return isInternalRole(user.role) && user.firstName === SMARTGATE_USER_FIRST_NAME;
}

export function isInternalUser(user: Maybe<IUser>): boolean {
  return validateUser(user) && isUserRoleValid(user.role) && isInternalRole(user.role);
}

export function isNormalUser(user: Maybe<IUser>): boolean {
  return validateUser(user) && isUserRoleValid(user.role) && !isInternalUser(user);
}

export function isBaseUser(user: Maybe<IUser>): boolean {
  return validateUser(user) && isUserRoleValid(user.role) && user.role === UserRole.BASE;
}

export function isWarehouseUser(user: Maybe<IUser>): boolean {
  return (
    validateUser(user) &&
    isUserRoleValid(user.role) &&
    !isBaseUser(user) &&
    !isInternalUser(user) &&
    !isCarrierUser(user)
  );
}

export function isSelfCheckInUser(user: Maybe<IUser>): boolean {
  return (
    validateUser(user) && isUserRoleValid(user.role) && user.email === GATE_MANAGEMENT_USER_EMAIL
  );
}

export function isCarrierUser(user: Maybe<IUser>): boolean {
  return (
    validateUser(user) &&
    isUserRoleValid(user.role) &&
    [UserRole.CARRIER, UserRole.CARRIER_ADMIN].includes(user.role) &&
    !user.orgId
  );
}

export function isYardDriverUser(user: Maybe<IUser>): boolean {
  return validateUser(user) && isUserRoleValid(user.role) && user.role === UserRole.YARD_DRIVER;
}

export function isOwner(user: Maybe<IUser>): boolean {
  return validateUser(user) && isUserRoleValid(user.role) && user.role === UserRole.OWNER;
}

export function doesUserHaveOrgRole(user: Maybe<IUser>): boolean {
  const validRoles = [
    UserRole.OWNER,
    UserRole.ADMIN,
    UserRole.OPERATOR,
    UserRole.ATTENDANT,
    UserRole.SPECTATOR,
    UserRole.YARD_DRIVER
  ];

  return validateUser(user) && validRoles.includes(user.role);
}

// Internal users may impersonate another user via Wormhole
export function isLoggedInAsAnotherUser(user: Maybe<IUser>) {
  return (
    validateUser(user) &&
    user.id !== userConstants.odcInternalUserId &&
    isUUID(user?.internalLoginUserId)
  );
}

export function getAuthenticatedUserId(user: Maybe<IUser>): Maybe<string> {
  if (isNil(user)) {
    throw new Error(`Core: getAuthenticatedUserId(): invalid User ${user}`);
  }
  if (isLoggedInAsAnotherUser(user)) {
    return user.internalLoginUserId;
  }
  if (isNil(user)) {
    throw new Error(`Core: getAuthenticatedUserId(): invalid User ${user}`);
  }
  return user.id;
}

export function canUserReadSubscriptions(user: Maybe<IUser>): boolean {
  return validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.ADMIN);
}

export function canUserDeleteDocks(user: Maybe<IUser>): boolean {
  return isInternalUser(user) || canUserCreateDocks(user);
}

export function canUserCreateDocks(user: Maybe<IUser>): boolean {
  return isInternalUser(user) || (doesUserHaveOrgRole(user) && canUserUpdateWarehouse(user));
}

export function canUserUpdateDocks(user: Maybe<IUser>): boolean {
  return canUserCreateDocks(user);
}

export function canUserInviteUsers(user: Maybe<IUser>): boolean {
  return doesUserHaveOrgRole(user) && canUserUpdateWarehouse(user);
}

export function canUserCreateLoadTypes(user: Maybe<IUser>): boolean {
  return doesUserHaveOrgRole(user) && canUserUpdateWarehouse(user);
}

export function canUserUpdateLoadTypes(user: Maybe<IUser>): boolean {
  return canUserDeleteLoadTypes(user);
}

export function canUserDeleteLoadTypes(user: Maybe<IUser>): boolean {
  return isInternalUser(user) || canUserCreateLoadTypes(user);
}

export function canUserBookInThePast(user: Maybe<IUser>): boolean {
  return validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.OPERATOR);
}

export function canUserUpdateFavoriteCarriers(user: IUser, orgId: string): boolean {
  return isUserRoleGreaterOrEqual(user.role, UserRole.OPERATOR) && user.orgId === orgId;
}

export function canUserUpdateCustomTags(user: Maybe<IUser>): boolean {
  return validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.ADMIN);
}

export function canUserReadNotificationConfig(user: Maybe<IUser>): boolean {
  return validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.ADMIN);
}

export function canUserCreateNotificationConfig(user: Maybe<IUser>): boolean {
  return validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.ADMIN);
}

export function canUserUpdateNotificationConfig(user: Maybe<IUser>): boolean {
  return validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.ADMIN);
}

export function isUserInSameOrg(user1: IUser, user2: IUser): boolean {
  return Boolean(
    validateUser(user1) &&
    validateUser(user2) &&
    user1.orgId &&
    user2.orgId &&
    user1.orgId === user2.orgId
  );
}

export function canUserCreateWarehouse(user: Maybe<IUser>): boolean {
  return Boolean(
    validateUser(user) &&
    !isUserWarehouseRestricted(user) &&
    isUserRoleGreaterOrEqual(user.role, UserRole.ADMIN) &&
    !hasEmptyOrgId(user)
  );
}

export function canUserUpdateWarehouse(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.ADMIN));
}

export function canUserDeleteWarehouse(user: Maybe<IUser>): boolean {
  return canUserUpdateWarehouse(user) && !isUserWarehouseRestricted(user);
}

// Custom form fields are org-scoped and shared by forms across every warehouse, so a
// warehouse-restricted admin must not change them on behalf of warehouses they cannot access.
export function canUserUpdateCustomFormFields(user: Maybe<IUser>): boolean {
  return Boolean(
    validateUser(user) &&
    isUserRoleGreaterOrEqual(user.role, UserRole.ADMIN) &&
    !isUserWarehouseRestricted(user)
  );
}

export function canUserCreateWarehouseGroup(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.ADMIN));
}

export function canUserUpdateWarehouseGroup(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.ADMIN));
}

export function canUserDeleteWarehouseGroup(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.ADMIN));
}

export function canUserModifyAppointment(user: IUser, body: Partial<IAppointment>): boolean {
  if (isAppointmentCancellation(body)) {
    return canUserCancelAppointment(user);
  }
  if (isStatusTimelineChange(body)) {
    return canUserModifyStatusTimeline(user);
  }
  return canUserUpdateAppointment(user, body);
}

export function canUserModifyApmtStatus(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && isUserRoleGreater(user.role, UserRole.SPECTATOR));
}

export function canUserModifyStatusTimeline(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && isUserRoleGreater(user.role, UserRole.SPECTATOR));
}

export function canUserDeleteAppointment(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && isUserRoleGreater(user.role, UserRole.ATTENDANT));
}

export function canUserModifyCustomFormData(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && user.role !== UserRole.SPECTATOR);
}

export function canUserCancelAppointment(user: Maybe<IUser>): boolean {
  return Boolean(
    validateUser(user) && (isUserRoleGreater(user.role, UserRole.ATTENDANT) || isCarrierUser(user))
  );
}

export function canUserCreateAppointment(user: Maybe<IUser>): boolean {
  return Boolean(
    (validateUser(user) && isUserRoleGreater(user.role, UserRole.ATTENDANT)) || isCarrierUser(user)
  );
}

export function canUserCreateReserve(user: Maybe<IUser>): boolean {
  return canUserCreateAppointment(user) && !isCarrierUser(user);
}

export function canUserUpdateStatus(user: IUser, appt?: Partial<IAppointment>): boolean {
  const apptBody: Partial<IAppointment> = isNil(appt) ? {} : appt;
  if (isUserRoleLessOrEqual(user.role, UserRole.ATTENDANT) && !isCarrierUser(user)) {
    return isStatusOnlyChange(apptBody);
  }
  return isStatusChange(apptBody);
}

export function canUserUpdateAppointment(user: IUser, appt?: Partial<IAppointment>): boolean {
  const apptBody: Partial<IAppointment> = isNil(appt) ? {} : appt;
  if (hasOnlyCCEmails(apptBody)) {
    return canUserUpdateAppointmentEmailCCs(user);
  }
  if (hasOnlyTags(apptBody)) {
    return canUserUpdateAppointmentTags(user);
  }
  if (canUserUpdateStatus(user, apptBody)) {
    return canUserModifyApmtStatus(user);
  }
  return canUserCreateAppointment(user);
}

export function canUserUpdateAppointmentEmailCCs(user: Maybe<IUser>): boolean {
  return Boolean(
    (validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.ATTENDANT)) ||
    isCarrierUser(user)
  );
}

export function canUserUpdateAppointmentTags(user: Maybe<IUser>): boolean {
  return Boolean(
    (validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.ATTENDANT)) ||
    isCarrierUser(user)
  );
}

export function canUserDeleteUserById(user: IUser, deletedUserId: string): boolean {
  return Boolean(
    validateUser(user) &&
    isUserRoleGreater(user.role, UserRole.OPERATOR) &&
    deletedUserId !== user.id
  );
}

export function canUserDeleteUser(user: IUser, deletedUser: IUser): boolean {
  return Boolean(
    validateUser(deletedUser) &&
    canUserDeleteUserById(user, deletedUser.id) &&
    isUserRoleLessOrEqual(deletedUser.role, user.role)
  );
}

export function canUserSendChatMessage(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && isUserRoleGreater(user.role, UserRole.ATTENDANT));
}

export function canUserChangeChatOpenStatus(user: Maybe<IUser>): boolean {
  return canUserSendChatMessage(user);
}

export function canUserChangeDriverPhone(user: Maybe<IUser>): boolean {
  return canUserSendChatMessage(user);
}

export function getUserRoles(): string[] {
  return Object.keys(UserRole);
}

export function getUserRoleValues(): string[] {
  return Object.values(UserRole);
}

export function compareUserRoles(role1: UserRole, role2: UserRole): number {
  const roleValues = getUserRoleValues();

  validateRole(role1);
  validateRole(role2);

  const index1 = roleValues.indexOf(role1);
  const index2 = roleValues.indexOf(role2);

  return index2 - index1;
}

export function isUserRoleGreaterOrEqual(role1: UserRole, role2: UserRole): boolean {
  return compareUserRoles(role1, role2) >= 0;
}

export function isUserRoleLessOrEqual(role1: UserRole, role2: UserRole): boolean {
  return compareUserRoles(role1, role2) <= 0;
}

export function isUserRoleLess(role1: UserRole, role2: UserRole): boolean {
  return !isUserRoleGreaterOrEqual(role1, role2);
}

export function isUserRoleGreater(role1: UserRole, role2: UserRole): boolean {
  return !isUserRoleLessOrEqual(role1, role2);
}

export function isUserRoleValid(role: UserRole): boolean {
  return Boolean(role && getUserRoleValues().includes(role));
}

export function isUserValid(user: Maybe<IUser>): boolean {
  const validUserFields = ['id', 'email', 'orgId', 'companyId', 'role'];
  return Boolean(
    user instanceof Object && Object.keys(user).find(k => validUserFields.includes(k))
  );
}

export function isPasswordResetRequired(user: Partial<IUser>): boolean {
  return Boolean(user.passwordResetRequired);
}

export function validateUser(user: Maybe<IUser>): user is IUser {
  if (!isUserValid(user)) {
    throw new Error(`Core: validateUser(): invalid User ${user}`);
  }
  return true;
}

export function validateRole(role: UserRole): role is UserRole {
  if (!isUserRoleValid(role)) {
    throw new Error(`Core: validateRole(): invalid User Role ${role}`);
  }
  return true;
}

export function canUserReadOrgData(user: IUser, orgId: string): boolean {
  return Boolean(
    isInternalUser(user) ||
    (isUserRoleGreaterOrEqual(user.role, UserRole.SPECTATOR) && user.orgId === orgId)
  );
}

export function canUserAccessOrg(user: IUser, orgId: string): boolean {
  return isInternalUser(user) || (!isNil(user.orgId) && user.orgId === orgId);
}

export function canUserDeleteOrg(user: IUser, orgId: string): boolean {
  return Boolean(
    isInternalUser(user) ||
    (isUserRoleGreaterOrEqual(user.role, UserRole.OWNER) && user.orgId === orgId)
  );
}

export function canUserCreateOrg(user: Maybe<IUser>): boolean {
  return Boolean(
    validateUser(user) &&
    hasEmptyOrgId(user) &&
    ![UserRole.CARRIER_ADMIN, UserRole.CARRIER].includes(user.role)
  );
}

export function canUserUpdateOrg(user: IUser, orgId: string): boolean {
  return Boolean(
    isInternalUser(user) ||
    (isUserRoleGreaterOrEqual(user.role, UserRole.ADMIN) &&
      !isUserWarehouseRestricted(user) &&
      user.orgId === orgId)
  );
}

export function canUserCreateUser(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && isUserRoleGreater(user.role, UserRole.OPERATOR));
}

export function canUserUpdateUser(user: IUser, userId: string): boolean {
  return (
    user.id === userId || (validateUser(user) && isUserRoleGreater(user.role, UserRole.OPERATOR))
  );
}

export function doesWarehouseAccessAllowUpdate(loggedInUser: IUser, targetUser: IUser): boolean {
  if (!isUserWarehouseRestricted(loggedInUser)) {
    return true;
  }
  const loggedInUserWarehouses = loggedInUser.warehouseAccessList;
  if (!Array.isArray(loggedInUserWarehouses)) {
    throw new Error(
      `Core: doesWarehouseAccessAllowUpdate(): warehouseAccessList must be an array for user ${loggedInUser.id}`
    );
  }

  if (!isUserWarehouseRestricted(targetUser)) {
    return false;
  }

  const targetUserWarehouses = targetUser.warehouseAccessList;
  if (!Array.isArray(targetUserWarehouses)) {
    throw new Error(
      `Core: doesWarehouseAccessAllowUpdate(): warehouseAccessList must be an array for user ${targetUser.id}`
    );
  }

  // Make sure targetUserWarehouses is a subset of the loggedInUserWarehouses
  return targetUserWarehouses.every((wId: NovaId) => loggedInUserWarehouses.includes(wId));
}

export function isRestrictedUserAttemptingToUnRestrictUser(
  loggedInUser: IUser,
  targetUser: IUser,
  dto: Partial<IUser>
): boolean {
  return (
    isUserWarehouseRestricted(loggedInUser) &&
    isUserWarehouseRestricted(targetUser) &&
    (dto.warehouseAccessList === null || dto.warehouseAccessList?.length === 0)
  );
}

export function canUserCreateCarrier(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.OPERATOR));
}

export function canUserCreateReportSearch(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.OPERATOR));
}

export function canUserDeleteReportSearch(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.OPERATOR));
}

export function canUserUpdateCarrier(user: Maybe<IUser>): boolean {
  return isInternalUser(user);
}

export function canRole1UpdateUser2ToRole3(
  loggedInUserRole: UserRole,
  updatedUser: IUser,
  newUserRole: UserRole
): boolean {
  if (isStrictInternalRole(loggedInUserRole)) {
    const isInternalPromotingValidEmailInternal =
      isStrictInternalRole(newUserRole) &&
      isUserRoleGreater(loggedInUserRole, updatedUser.role) &&
      isValidInternalEmail(updatedUser.email);

    const isInternalPromotingAnyUser =
      !isInternalRole(newUserRole) && isUserRoleGreaterOrEqual(loggedInUserRole, updatedUser.role);

    return isInternalPromotingValidEmailInternal || isInternalPromotingAnyUser;
  }

  return (
    isUserRoleGreaterOrEqual(loggedInUserRole, newUserRole) &&
    isUserRoleGreaterOrEqual(loggedInUserRole, updatedUser.role)
  );
}

export function canUserChangeOrgCarrierSettings(user: Maybe<IUser>): boolean {
  return validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.OPERATOR);
}

export function canUserReadOrgCarrierSettings(user: Maybe<IUser>): boolean {
  return validateUser(user);
}

export function canUserConfigureSaml(user: Maybe<IUser>): boolean {
  return Boolean(
    validateUser(user) &&
    isWarehouseUser(user) &&
    !isUserWarehouseRestricted(user) &&
    isUserRoleGreaterOrEqual(user.role, UserRole.ADMIN)
  );
}

export function canUserReadSelfSamlConfig(user: Maybe<IUser>): boolean {
  return Boolean(
    validateUser(user) &&
    isWarehouseUser(user) &&
    isUserRoleGreaterOrEqual(user.role, UserRole.OPERATOR)
  );
}

export interface PasswordValidationRuleResult {
  error: string;
  message: string;
  passed: boolean;
}

export function getPasswordValidationErrors(password: string): PasswordValidationRuleResult[] {
  const schema = new PasswordValidator();
  schema
    .is()
    .min(8) // Minimum length 8
    .has()
    .uppercase() // Must have uppercase letters
    .has()
    .lowercase() // Must have lowercase letters
    .has()
    .symbols() // Must have special charaters
    .has()
    .digits(1); // Must have at least 1 digit
  const passwordValidationErrorsRaw = schema.validate(password, { list: true });
  if (!Array.isArray(passwordValidationErrorsRaw) || !passwordValidationErrorsRaw.every(isString)) {
    throw new Error(
      'Core: getPasswordValidationErrors(): expected string[] from password validation list mode'
    );
  }
  const passwordValidationErrors = passwordValidationErrorsRaw;

  const returnedErrors: PasswordValidationRuleResult[] = [];

  returnedErrors.push({
    error: 'min',
    message: 'Must have at least 8 characters',
    passed: !passwordValidationErrors.includes('min')
  });
  returnedErrors.push({
    error: 'uppercase-and-lowercase',
    message: 'Must contain upper-case and lower-case',
    passed: !(
      passwordValidationErrors.includes('lowercase') ||
      passwordValidationErrors.includes('uppercase')
    )
  });
  returnedErrors.push({
    error: 'one-letter',
    message: 'Must contain at least one letter',
    passed: !(
      passwordValidationErrors.includes('lowercase') &&
      passwordValidationErrors.includes('uppercase')
    )
  });
  returnedErrors.push({
    error: 'symbols',
    message: 'Must contain at least one special character',
    passed: !passwordValidationErrors.includes('symbols')
  });
  returnedErrors.push({
    error: 'one-number',
    message: 'Must contain at least one number',
    passed: !passwordValidationErrors.includes('digits')
  });

  return returnedErrors;
}

export function getTranslatedPasswordErrors(password: string) {
  const errors = getPasswordValidationErrors(password);
  return errors.map(error => {
    return {
      ...error,
      message: t(`core.user.errors.${camelCase(error.message)}`)
    };
  });
}

/**
 * Generates the password reset required error message based on the user's email
 * and the time between the last password reset email and the current time.
 *
 * @param {string} userEmail - The user's email address.
 * @param {number} minutesBetweenLastEmailAndNow - The time between the last password reset email and the current time.
 * @returns {string} The formatted error message.
 */
export function getPasswordResetRequiredErrorMessage(
  userEmail: string,
  minutesBetweenLastEmailAndNow = 0
) {
  if (!userEmail || !isString(userEmail)) {
    throw new Error('invalid user email');
  }
  const errorMessage = `Your password must be reset for continued access to Opendock. A password reset email has been sent to ${userEmail}`;
  const additionalErrorMessage =
    minutesBetweenLastEmailAndNow > 0 &&
    minutesBetweenLastEmailAndNow <= PASSWORD_RESET_EMAIL_THROTTLE_IN_MINUTES
      ? ` within the last ${PASSWORD_RESET_EMAIL_THROTTLE_IN_MINUTES} minutes`
      : '';
  return `${errorMessage}${additionalErrorMessage}.`;
}

/**
 * Calculates the time difference in minutes between the last password reset email sent
 * and the current time.
 *
 * @param {passwordResetEmailSentAt: Date} user - The user object with password reset email information.
 * @returns {number} The time difference in minutes.
 */
export function getDiffBetweenPasswordResetEmailAndNow(passwordResetEmailSentAt: Date) {
  let lastEmailSentAt = passwordResetEmailSentAt;
  if (isDateString(passwordResetEmailSentAt)) {
    lastEmailSentAt = DateTime.fromISO(String(passwordResetEmailSentAt)).toJSDate();
  }

  if (!isDate(lastEmailSentAt)) {
    throw new Error('invalid Date');
  }
  let minutesBetweenLastEmailAndNow = 0;
  if (lastEmailSentAt) {
    minutesBetweenLastEmailAndNow = diffBetweenDates({
      startDateStr: lastEmailSentAt,
      endDateStr: new Date(),
      diffUnit: 'minutes'
    });
  }
  return minutesBetweenLastEmailAndNow;
}

export function canUserCreateSpot(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.ADMIN));
}

export function canUserUpdateSpot(user: Maybe<IUser>): boolean {
  return canUserCreateSpot(user);
}

export function canUserDeleteSpot(user: Maybe<IUser>): boolean {
  return canUserCreateSpot(user);
}

export function canUserCreateSpotArea(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.ADMIN));
}

export function canUserUpdateSpotArea(user: Maybe<IUser>): boolean {
  return canUserCreateSpotArea(user);
}

export function canUserDeleteSpotArea(user: Maybe<IUser>): boolean {
  return canUserCreateSpotArea(user);
}

export function canUserCreateSpotAssignment(user: Maybe<IUser>): boolean {
  return Boolean(validateUser(user) && isUserRoleGreaterOrEqual(user.role, UserRole.OPERATOR));
}

export function canUserUpdateSpotAssignment(user: Maybe<IUser>): boolean {
  return canUserCreateSpotAssignment(user);
}

export function canUserDeleteSpotAssignment(user: Maybe<IUser>): boolean {
  return canUserCreateSpotAssignment(user);
}
